import { UTCDate } from '@date-fns/utc';
import { isSameDay } from 'date-fns';
import React from 'react';
import { getDateRangesFromDays, getDaysBetweenDates } from 'utils/utils';
import { CalendarDateRange, DateRangePickerVariant } from './types';
import { getWeekRange, normalizeDate, normalizeRange } from './utils';

interface UseCalendarSelectionProps {
    periods: CalendarDateRange[];
    variant?: DateRangePickerVariant;
    onChange: (periods: CalendarDateRange[]) => void;
    multiplePeriods: boolean;
    onSelectionComplete?: () => void;
}

interface UseCalendarSelectionResult {
    displayedPeriods: CalendarDateRange[];
    previewPeriod: CalendarDateRange | null;
    handleDayClick: (day: UTCDate) => void;
    handleDayHover: (day: UTCDate | null) => void;
}

function getSingleRangeFromStartClick(
    day: UTCDate,
    currentPeriod: CalendarDateRange | null
): CalendarDateRange {
    if (currentPeriod && day.getTime() <= currentPeriod.end.getTime()) {
        return normalizeRange(day, currentPeriod.end);
    }

    return normalizeRange(day, day);
}

function toggleDayInPeriods(
    periods: CalendarDateRange[],
    day: UTCDate
): CalendarDateRange[] {
    const days = periods.flatMap(period =>
        getDaysBetweenDates(period.start, period.end)
    );
    const index = days.findIndex(value => isSameDay(value, day));
    if (index === -1) {
        days.push(day);
    } else {
        days.splice(index, 1);
    }
    days.sort((a, b) => a.getTime() - b.getTime());
    return getDateRangesFromDays(days);
}

export function useCalendarSelection({
    periods,
    variant = 'range',
    onChange,
    multiplePeriods,
    onSelectionComplete,
}: UseCalendarSelectionProps): UseCalendarSelectionResult {
    const [pendingStart, setPendingStart] = React.useState<UTCDate | null>(
        null
    );
    const [hoveredDay, setHoveredDay] = React.useState<UTCDate | null>(null);

    React.useEffect(() => {
        if (variant !== 'range') {
            setPendingStart(null);
        }
    }, [variant]);

    const currentSinglePeriod = React.useMemo(() => {
        if (multiplePeriods || periods.length === 0) {
            return null;
        }

        return normalizeRange(periods[0].start, periods[0].end);
    }, [multiplePeriods, periods]);

    const isPickingEnd = !multiplePeriods && pendingStart !== null;

    const previewPeriod = React.useMemo(() => {
        if (multiplePeriods || !hoveredDay) {
            return null;
        }

        if (variant === 'day') {
            return normalizeRange(hoveredDay, hoveredDay);
        }

        if (variant === 'week') {
            return getWeekRange(hoveredDay);
        }

        if (isPickingEnd && pendingStart) {
            return normalizeRange(pendingStart, hoveredDay);
        }

        if (!currentSinglePeriod) {
            return null;
        }

        if (hoveredDay.getTime() <= currentSinglePeriod.end.getTime()) {
            return normalizeRange(hoveredDay, currentSinglePeriod.end);
        }

        return normalizeRange(hoveredDay, hoveredDay);
    }, [
        currentSinglePeriod,
        hoveredDay,
        isPickingEnd,
        multiplePeriods,
        pendingStart,
        variant,
    ]);

    const displayedPeriods = React.useMemo(() => {
        if (multiplePeriods) {
            return periods;
        }

        if (previewPeriod) {
            return [];
        }

        return currentSinglePeriod ? [currentSinglePeriod] : [];
    }, [currentSinglePeriod, multiplePeriods, periods, previewPeriod]);

    const handleDayClick = React.useCallback(
        (day: UTCDate) => {
            const normalizedDay = normalizeDate(day);

            if (multiplePeriods) {
                onChange(toggleDayInPeriods(periods, normalizedDay));
                return;
            }

            if (variant === 'week' || variant === 'day') {
                onChange([
                    variant === 'week'
                        ? getWeekRange(normalizedDay)
                        : normalizeRange(normalizedDay, normalizedDay),
                ]);
                onSelectionComplete?.();
                setPendingStart(null);
                setHoveredDay(null);
                return;
            }

            if (isPickingEnd && pendingStart) {
                onChange([normalizeRange(pendingStart, normalizedDay)]);
                onSelectionComplete?.();
                setPendingStart(null);
                setHoveredDay(null);
                return;
            }

            onChange([
                getSingleRangeFromStartClick(
                    normalizedDay,
                    currentSinglePeriod
                ),
            ]);
            setPendingStart(normalizedDay);
            setHoveredDay(null);
        },
        [
            currentSinglePeriod,
            isPickingEnd,
            multiplePeriods,
            onChange,
            onSelectionComplete,
            pendingStart,
            periods,
            variant,
        ]
    );

    const handleDayHover = React.useCallback(
        (day: UTCDate | null) => {
            if (multiplePeriods) {
                return;
            }

            setHoveredDay(day ? normalizeDate(day) : null);
        },
        [multiplePeriods]
    );

    return {
        displayedPeriods,
        previewPeriod,
        handleDayClick,
        handleDayHover,
    };
}
